import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiFetch, apiJson } from '../../lib/api';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";

interface InviteDetail {
  token: string;
  org_id: string;
  org_name: string;
  github_login: string;
  org_role: string;
  status: string;
  expires_at: string;
}

export function InviteAccept({ inviteToken }: { inviteToken: string }) {
  const { user, token } = useAuth();
  const [invite, setInvite] = useState<InviteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    fetch(`/api/invitations/${inviteToken}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
        } else {
          setInvite(data);
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load invitation');
        setLoading(false);
      });
  }, [inviteToken]);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/invitations/${inviteToken}/accept`, {
        method: 'POST',
      });
      const data = await resp.json();
      if (data.ok) {
        setAccepted(true);
      } else {
        setError(data.error || 'Failed to accept invitation');
      }
    } catch {
      setError('Failed to accept invitation');
    }
    setAccepting(false);
  };

  const handleLoginThenAccept = () => {
    localStorage.setItem('opentool_pending_invite', inviteToken);
    window.location.hash = '';
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: '#0d0d0d',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT, color: '#6272a4', fontSize: 13,
      }}>
        Loading invitation...
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#0d0d0d',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: FONT,
    }}>
      {/* Subtle grid background */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        pointerEvents: 'none',
      }} />

      <div style={{
        background: '#1e1e2e',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 8,
        padding: '36px 32px',
        width: 420,
        maxWidth: '90vw',
        textAlign: 'center',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        {accepted ? (
          <>
            <div style={{ fontSize: 36, marginBottom: 16, color: '#50fa7b' }}>&#10003;</div>
            <h2 style={{ color: '#50fa7b', fontSize: 18, fontWeight: 600, margin: '0 0 8px', fontFamily: FONT }}>
              Invitation Accepted
            </h2>
            <p style={{ color: '#6272a4', fontSize: 13, margin: '0 0 24px' }}>
              You are now a member of <span style={{ color: '#f8f8f2' }}>{invite?.org_name}</span>
            </p>
            <button
              onClick={() => { window.location.hash = 'home'; window.location.reload(); }}
              style={{
                padding: '10px 24px', background: '#50fa7b', color: '#0d0d0d',
                border: 'none', borderRadius: 4, fontFamily: FONT, fontSize: 13,
                fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s ease',
              }}
            >
              Go to Dashboard
            </button>
          </>
        ) : error && !invite ? (
          <>
            <h2 style={{ color: '#ff5555', fontSize: 18, fontWeight: 600, margin: '0 0 12px', fontFamily: FONT }}>
              Invalid Invitation
            </h2>
            <p style={{ color: '#6272a4', fontSize: 13 }}>{error}</p>
          </>
        ) : invite ? (
          <>
            <h2 style={{ color: '#f8f8f2', fontSize: 18, fontWeight: 600, margin: '0 0 8px', fontFamily: FONT }}>
              Organization Invitation
            </h2>
            <p style={{ color: '#6272a4', fontSize: 11, margin: '0 0 24px' }}>
              You have been invited to join an organization
            </p>

            <div style={{
              background: '#282a36',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 8,
              padding: '16px 24px',
              marginBottom: 24,
              textAlign: 'left',
            }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: '#6272a4', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Organization</div>
                <div style={{ fontSize: 15, color: '#f8f8f2', fontWeight: 600 }}>{invite.org_name}</div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: '#6272a4', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</div>
                <span style={{
                  fontSize: 11, color: '#bd93f9', padding: '2px 10px',
                  background: 'rgba(189,147,249,0.1)', borderRadius: 4, border: '1px solid rgba(189,147,249,0.25)',
                  fontFamily: FONT,
                }}>
                  {invite.org_role}
                </span>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#6272a4', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Invited as</div>
                <div style={{ fontSize: 13, color: '#8be9fd' }}>@{invite.github_login}</div>
              </div>
            </div>

            {invite.status !== 'pending' ? (
              <p style={{ color: '#6272a4', fontSize: 13 }}>
                This invitation has already been {invite.status}.
              </p>
            ) : !user ? (
              <button
                onClick={handleLoginThenAccept}
                style={{
                  width: '100%', padding: '12px 24px', background: '#282a36',
                  border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4, color: '#f8f8f2',
                  fontSize: 13, fontFamily: FONT, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all 0.15s ease',
                }}
              >
                Sign in to Accept
              </button>
            ) : (
              <button
                onClick={handleAccept}
                disabled={accepting}
                style={{
                  width: '100%', padding: '12px 24px', background: '#50fa7b',
                  color: '#0d0d0d', border: 'none', borderRadius: 4,
                  fontSize: 13, fontFamily: FONT, fontWeight: 600,
                  cursor: accepting ? 'wait' : 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {accepting ? 'Accepting...' : 'Accept Invitation'}
              </button>
            )}

            {error && (
              <p style={{
                color: '#ff5555', fontSize: 12, marginTop: 12,
                padding: '8px 12px', background: 'rgba(255,85,85,0.08)', borderRadius: 8,
                border: '1px solid rgba(255,85,85,0.2)', fontFamily: FONT,
              }}>
                {error}
              </p>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
